import FacebookLogin from "@greatsumini/react-facebook-login";
import { useGoogleLogin } from "@react-oauth/google";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useRecaptcha } from "../../../contexts/RecaptchaContext.jsx";
import AuthWrapper from "../../../components/AuthWrapper/index.jsx";
import { Button, Input, TextView } from "../../../components/index.js";
import {
  useFacebook,
  useGoogleToken,
  useLogin,
  useForgotSendPhoneOtp,
  useForgotVerifyPhoneOtp,
  useVerifyPhoneOtp,
} from "../../../services/mutations.js";
import { ASSETS, PATH } from "../../../utils/config.js";
import { colors } from "../../../utils/theme.js";
import * as Styled from "../styles.js";
import ErrorModal from "./ErrorModal.jsx";
import { toast } from "react-toastify";
import OTPModal from "../../../components/PasswordAlert/index.js";
import CustomPhoneInput from "../../../components/PhoneInput/index.jsx";
import { OTPContainer } from "../../../components/Settings/Password/styles.js";
import styled from "styled-components";
import {
  StyledDialogActions,
  ResendButton,
} from "../../../components/Settings/Password/styles.js";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import { userProfileSuccess } from "../../../redux/actions/user.js";
import { requestNotificationPermission } from "../../../utils/firebase.js";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import {
  StyledDialogContent,
  StyledDialogTitle,
  HeaderIcon,
  StyledTermsContent,
  TermsButton,
} from "./styles.js";
import { TermsAndConditionsAPI } from "../../../components/Settings/LegalTabs/TermsAndConditionsAPI.jsx";
import { PrivacyPolicyAPI } from "../../../components/Settings/LegalTabs/PrivacyPolicyAPI.jsx";
import { useGetTermsAndConditions } from "../../../services/queries.js";
import EmailVerificationPopup from "../../../components/EmailVerificationPopup/index.jsx";

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightBorder};
  margin-top: 20px;
`;

// ReCAPTCHA Site Key - Get from environment variable
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// ReCAPTCHA Feature Flag - Controls whether reCAPTCHA is enabled
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

console.log("RECAPTCHA_ENABLED", RECAPTCHA_ENABLED);

// Creating schema
const schema = Yup.object().shape({
  email: Yup.string()
    .required("Email is a Required field")
    .email("Please Enter a Valid Email"),
  password: Yup.string()
    .required("Password is a Required field")
    .min(8, "Password must be at least 8 characters"),
  // Note: recaptchaToken is NOT in validation schema because it's handled programmatically
});

const Login = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useRecaptcha();

  const getRedirectPath = (userId) => {
    const from = location.state?.from;
    if (from) {
      return `${from.pathname}${from.search || ""}${from.hash || ""}`;
    }
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      return redirectParam;
    }
    return `/gallery/${userId}`;
  };

  let userLogin = useLogin();
  let getGoogleToken = useGoogleToken();
  let getFacebookToken = useFacebook();
  const sendOtpToPhone = useForgotSendPhoneOtp();
  const verifyPhoneOtp = useForgotVerifyPhoneOtp();

  // Call the API for terms and conditions data
  const {
    data: termsData,
    isLoading: termsLoading,
    error: termsError,
  } = useGetTermsAndConditions();

  const [showRetryPrompt, setShowRetryPrompt] = useState(false);
  const [email, setEmail] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendLoad, setResendLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showEmailVerificationPopup, setShowEmailVerificationPopup] =
    useState(false);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleRetry = (values) => {
    setShowRetryPrompt(false);
    handleSubmit(values);
  };

  const handleChange = (value, index) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && otp[index] === "") {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    } else if (event.key === "Enter" && otp.every((char) => char !== "")) {
      // Check if all OTP fields are filled before verifying
      verifyOtp();
    }
  };

  const loginWithFacebook = (response) => {
    getFacebookToken.mutate(
      { access_token: response.accessToken },
      {
        onSuccess: (res) => {
          console.log("Facebook Login", res);
          const userId = res?.data?.user?.id;
          nav(getRedirectPath(userId));
        },
        onError: (err) => {
          setShowRetryPrompt(true);
          console.log("Facebook login failed", err);
        },
      },
    );
  };

  const [fcmToken, setFcmToken] = useState(null);

  useEffect(() => {
    requestNotificationPermission().then((token) => {
      if (token) {
        console.log("FCM Token received:", token);
        setFcmToken(token);
      }
    });
  }, []);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      getGoogleToken.mutate(
        { access_token: codeResponse.access_token },
        {
          onSuccess: (res) => {
            console.log("Google Login", res);
            const userId = res?.data?.user?.id;
            nav(getRedirectPath(userId));
          },
          onError: (err) => {
            setShowRetryPrompt(true);
            console.log("Google login failed", err);
          },
        },
      );
    },
  });

  const handleSubmit = async (values, formikHelpers) => {
    let recaptchaToken = null;

    // Check if reCAPTCHA is enabled via feature flag
    if (RECAPTCHA_ENABLED) {
      // Check if reCAPTCHA is configured
      if (!RECAPTCHA_SITE_KEY) {
        console.warn(
          "reCAPTCHA site key not configured. Proceeding without reCAPTCHA verification.",
        );
        // Continue without reCAPTCHA for development/testing
      } else if (executeRecaptcha) {
        try {
          // Execute reCAPTCHA with 'login' action
          console.log("Executing reCAPTCHA with 'login' action...");
          recaptchaToken = await executeRecaptcha("login");
          console.log("reCAPTCHA token generated successfully");
        } catch (error) {
          console.error("reCAPTCHA execution failed:", error);
          toast.error("reCAPTCHA verification failed. Please refresh the page.");
          return;
        }
      } else {
        console.warn("reCAPTCHA not yet available");
        toast.error("reCAPTCHA not loaded. Please refresh the page.");
        return;
      }
    }

    console.log(
      RECAPTCHA_ENABLED
        ? "reCAPTCHA token generated, proceeding with login..."
        : "reCAPTCHA feature flag disabled, proceeding with login...",
    );

    // Proceed with login if recaptcha is verified
    let token = fcmToken;
    if (!token) {
      try {
        token = await requestNotificationPermission();
        setFcmToken(token);
      } catch (error) {
        console.error("Notification permission request failed:", error);
      }
    }
    setEmail(values.email);

    setIsLoggingIn(true);

    // Build payload with backend-friendly field names
    const updatedValues = {
      email: values.email,
      password: values.password,
      device_type: "Web",
      device_token: token,
      // Conditionally add recaptcha_token (snake_case) if reCAPTCHA is enabled
      ...(RECAPTCHA_ENABLED && recaptchaToken
        ? { recaptcha_token: recaptchaToken }
        : {}),
    };
    userLogin.mutate(updatedValues, {
      onSuccess: (response) => {
        if (response.data["2FA"].phone_enabled) {
          setCurrentUser(response.data.user);
          setShowModal(true);
        } else {
          dispatch(
            userProfileSuccess({
              is2faEnabled: false,
            }),
          );
          nav(getRedirectPath(response.data.user.id));
          localStorage.setItem("show_walkthrough", "true");
        }
        setIsLoggingIn(false);
      },
      onError: (err) => {
        if (err.response.data?.data?.is_verified === false) {
          setIsLoggingIn(false);
          setShowEmailVerificationPopup(true);
          return;
        }
        if (err.response.data?.message) {
          toast.error(err.response.data.message);
        } else {
          const key = Object.keys(err.response.data)[0];
          toast.error(err.response.data[key][0]);
          if (err.request.status === 500 || err.request.status === 502) {
            setShowRetryPrompt(true);
          }
        }
        setIsLoggingIn(false);
      },
    });
  };

  const sendOtp = () => {
    setLoading(true);
    try {
      sendOtpToPhone.mutateAsync(
        {
          email: email,
        },
        {
          onSuccess: (response) => {
            toast.success("An OTP is sent to your phone number!");
            setLoading(false);
          },
          onError: (err) => {
            console.log(err);
            toast.error(err.response.data.message);
            setLoading(false);
          },
        },
      );
    } catch (err) {
      toast.error(err.response.data.message);
    }
  };

  const verifyOtp = () => {
    setLoading(true);
    const otpCode = otp.join("");

    verifyPhoneOtp.mutateAsync(
      {
        otp: otpCode,
        email,
        current_state: "login",
      },
      {
        onSuccess: (response) => {
          let token = response?.data?.token;
          let user = currentUser;
          localStorage.setItem("keyCloak", JSON.stringify({ token, user }));
          dispatch(
            userProfileSuccess({
              is2faEnabled: true,
            }),
          );
          setLoading(false);
          closeModal();
          nav(getRedirectPath(user.id));
          localStorage.setItem("show_walkthrough", "true");
        },
        onError: (err) => {
          console.log("err", err);
          toast.error(err.response.data.message);
          setLoading(false);
        },
      },
    );
  };

  const closeTermsDialog = () => {
    setShowTerms(false);
    setShowPrivacy(false);
    setOpenDialog(false);
  };

  return (
    <AuthWrapper title="Welcome Back!">
      <>
        <Formik
          validationSchema={schema}
          initialValues={{
            email: "",
            password: "",
            checked: true,
          }}
          onSubmit={(values, formikHelpers) =>
            handleSubmit(values, formikHelpers)
          }
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <>
              <form noValidate onSubmit={handleSubmit}>
                <Styled.InputWrapper>
                  <Input
                    type="email"
                    name="email"
                    variant={"secondary"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    placeholder="Email"
                    containerStyle={{ marginBottom: 0 }}
                  />
                  <Styled.Error>
                    {
                      <>
                        {errors.email && touched.email && errors.email && (
                          <img
                            src={ASSETS.validation_error}
                            className="validation_error"
                            alt="validation error NP"
                          />
                        )}
                        <>{errors.email && touched.email && errors.email}</>
                      </>
                    }
                  </Styled.Error>
                </Styled.InputWrapper>

                <Styled.InputWrapper>
                  <Input
                    type={"password"}
                    name="password"
                    variant={"secondary"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    placeholder="Password"
                    containerStyle={{ marginBottom: 0 }}
                  />

                  <Styled.Error>
                    {
                      <>
                        {errors.password &&
                          touched.password &&
                          errors.password && (
                            <img
                              src={ASSETS.validation_error}
                              className="validation_error"
                              alt="validation error NP"
                            />
                          )}
                        <>
                          {errors.password &&
                            touched.password &&
                            errors.password}
                        </>
                      </>
                    }
                  </Styled.Error>
                </Styled.InputWrapper>

                <Styled.Row
                  style={{ marginTop: 10, justifyContent: "flex-end" }}
                >
                  {/* <Styled.Row>
                    <Styled.Checkbox
                      type="checkbox"
                      name="checked"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      defaultValue={true}
                      checked={values.checked}
                    />
                    <TextView>Remember Me</TextView>
                  </Styled.Row> */}
                  <a
                    href={PATH.FORGOT_PASSWORD}
                    style={{ textDecoration: "none" }}
                  >
                    <TextView>Forgot Password?</TextView>
                  </a>
                </Styled.Row>
                <Button
                  style={{ marginTop: 20, marginBottom: 20 }}
                  type="submit"
                  isLoading={isLoggingIn}
                >
                  Log In
                </Button>

                <ErrorModal
                  open={showRetryPrompt}
                  handleClose={() => setShowRetryPrompt(false)}
                  handleRetry={() => handleRetry(values)}
                />
              </form>
              <EmailVerificationPopup
                open={showEmailVerificationPopup}
                onClose={() => setShowEmailVerificationPopup(false)}
                email={values.email}
              />

              {/* <Styled.Separator>
                <Styled.Line />
                <TextView
                  style={{
                    padding: "0 10px",
                    position: "relative",
                    zIndex: 1,
                    width: 40,
                  }}
                >
                  OR
                </TextView>
                <Styled.Line />
              </Styled.Separator>

              <Styled.SocialBlock>
                <Styled.SocialItem onClick={loginWithGoogle}>
                  <img src={ASSETS.google2} />
                </Styled.SocialItem>

                <FacebookLogin
                  appId={"848410477295893"}
                  onSuccess={loginWithFacebook}
                  onFail={(error) => {
                    console.log("Login Failed!", error);
                  }}
                  render={({ onClick }) => (
                    <Styled.SocialItem onClick={onClick}>
                      <img src={ASSETS.fb} />
                    </Styled.SocialItem>
                  )}
                />

                <Styled.SocialItem>
                  <img src={ASSETS.apple} />
                </Styled.SocialItem>
              </Styled.SocialBlock> */}

              <TextView style={{ textAlign: "center" }}>
                Don't have an account?{" "}
                <span
                  onClick={() => nav("/signup")}
                  style={{
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: colors.PrimaryPurple,
                  }}
                >
                  Sign Up Here!
                </span>
              </TextView>

              <Styled.BottomView>
                <TermsButton
                  onClick={() => {
                    setShowPrivacy(true);
                    setOpenDialog(true);
                  }}
                >
                  Privacy Policy
                </TermsButton>
                <TermsButton
                  onClick={() => {
                    setShowTerms(true);
                    setOpenDialog(true);
                  }}
                >
                  Terms & Conditions
                </TermsButton>
              </Styled.BottomView>
            </>
          )}
        </Formik>

        <OTPModal
          open={showModal}
          handleClose={closeModal}
          icon={ASSETS.SMSNew}
          title={"SMS Authentication"}
        >
          <div>
            <TextView
              variant="subtitle"
              style={{ color: "#565556", fontWeight: 700 }}
            >
              {"Enter the OTP you received via text message on your phone."}
            </TextView>

            <OTPContainer>
              {otp.map((_, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  maxLength="1"
                  value={otp[index]}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </OTPContainer>

            <Separator />

            <StyledDialogActions>
              <Button
                isLoading={resendLoad}
                onClick={sendOtp}
                style={{ width: 60 }}
                variant={"secondary"}
              >
                Resend
              </Button>

              <Button
                onClick={verifyOtp}
                style={{ width: 60 }}
                isLoading={loading}
                disabled={otp.some((char) => char === "")}
              >
                Send
              </Button>
            </StyledDialogActions>
          </div>
        </OTPModal>
      </>

      {openDialog && (
        <Dialog
          fullWidth={true}
          maxWidth={"md"}
          open={openDialog}
          onClose={closeTermsDialog}
        >
          <StyledDialogContent>
            <StyledDialogTitle>
              {/* <HeaderIcon src={ASSETS.Insurance} /> */}
              {/* <TextView variant="title">
                {showTerms ? "iYuga's Terms and Conditions" : "Privacy Policy"}
              </TextView> */}
            </StyledDialogTitle>

            <StyledTermsContent>
              {showTerms ? (
                <TermsAndConditionsAPI
                  data={termsData}
                  isLoading={termsLoading}
                  error={termsError}
                />
              ) : (
                <PrivacyPolicyAPI
                  data={termsData}
                  isLoading={termsLoading}
                  error={termsError}
                />
              )}
            </StyledTermsContent>

            <Button
              style={{ width: "158px", alignSelf: "center" }}
              onClick={closeTermsDialog}
            >
              Close
            </Button>
          </StyledDialogContent>
        </Dialog>
      )}
    </AuthWrapper>
  );
};

export default Login;
